import React, { useState, useEffect } from 'react';
import { getAllQuestions } from '../services/questionsService';
import { getCommentsForQuestion, deleteComment, deleteReply } from '../services/questionsService';
import { isUserBlocked, getUserById } from '../services/userService';

function CommentManagement() {
  const [questions, setQuestions] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingComments, setLoadingComments] = useState(false);
  const [error, setError] = useState('');
  const [actionInProgress, setActionInProgress] = useState(false);
  const [userCache, setUserCache] = useState({});

  // Fetch all questions on component mount
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        setLoading(true);
        const result = await getAllQuestions();
        setQuestions(result.questions || []);
      } catch (err) {
        console.error("Error fetching questions:", err);
        setError("Failed to load questions. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, []);

  // Fetch comments when a question is selected
  useEffect(() => {
    if (!selectedQuestion) return;

    const fetchComments = async () => {
      try {
        setLoadingComments(true);
        const result = await getCommentsForQuestion(selectedQuestion, 1, 100);
        
        // Create a copy of comments with replies included
        let allComments = result.comments || [];
        
        // Add user status information
        const commentsWithUserStatus = await Promise.all(
          allComments.map(async comment => {
            // Check user cache first
            if (userCache[comment.userId]) {
              return {
                ...comment,
                userIsBlocked: userCache[comment.userId].isBlocked
              };
            }
            
            // Get user status
            try {
              const isBlocked = await isUserBlocked(comment.userId);
              
              // Cache the result
              setUserCache(prev => ({
                ...prev,
                [comment.userId]: { isBlocked }
              }));
              
              return {
                ...comment,
                userIsBlocked: isBlocked
              };
            } catch (err) {
              console.error("Error checking user blocked status:", err);
              return comment;
            }
          })
        );
        
        setComments(commentsWithUserStatus);
      } catch (err) {
        console.error("Error fetching comments:", err);
        setError("Failed to load comments. Please try again.");
      } finally {
        setLoadingComments(false);
      }
    };

    fetchComments();
  }, [selectedQuestion, userCache]);

  const handleDeleteComment = async (commentId) => {
    if (!commentId || !selectedQuestion) return;
    
    if (!window.confirm("Are you sure you want to delete this comment?")) {
      return;
    }
    
    try {
      setActionInProgress(true);
      
      // Use admin override (true as last parameter)
      await deleteComment(commentId, selectedQuestion, "admin", true);
      
      // Update local state
      setComments(comments.filter(comment => comment.id !== commentId));
      
      alert("Comment deleted successfully!");
    } catch (err) {
      console.error("Error deleting comment:", err);
      setError("Failed to delete comment. Please try again.");
    } finally {
      setActionInProgress(false);
    }
  };

  const handleDeleteReply = async (replyId, parentId) => {
    if (!replyId || !parentId) return;
    
    if (!window.confirm("Are you sure you want to delete this reply?")) {
      return;
    }
    
    try {
      setActionInProgress(true);
      
      // Use admin override (true as last parameter)
      await deleteReply(replyId, parentId, "admin", true);
      
      // Update local state
      setComments(prevComments => 
        prevComments.map(comment => 
          comment.id === parentId 
            ? {
                ...comment,
                replies: (comment.replies || []).filter(reply => reply.id !== replyId)
              } 
            : comment
        )
      );
      
      alert("Reply deleted successfully!");
    } catch (err) {
      console.error("Error deleting reply:", err);
      setError("Failed to delete reply. Please try again.");
    } finally {
      setActionInProgress(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    } catch (err) {
      return 'Invalid date';
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '20px' }}>Loading questions...</div>;
  }

  if (error) {
    return <div style={{ color: 'red', padding: '20px' }}>{error}</div>;
  }

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <label htmlFor="question-select" style={{ marginRight: '10px', fontWeight: 'bold' }}>
          Select Question:
        </label>
        <select 
          id="question-select"
          value={selectedQuestion || ''}
          onChange={(e) => setSelectedQuestion(e.target.value)}
          style={{ padding: '5px 10px', minWidth: '250px' }}
        >
          <option value="">-- Select a question --</option>
          {questions.map(question => (
            <option key={question.id} value={question.id}>
              {question.questionText}
            </option>
          ))}
        </select>
      </div>

      {selectedQuestion ? (
        <>
          {loadingComments ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>Loading comments...</div>
          ) : comments.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center' }}>No comments found for this question.</div>
          ) : (
            <div style={{ border: '1px solid #ddd', borderRadius: '4px' }}>
              <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd' }}>
                <h3 style={{ margin: 0 }}>Comments ({comments.length})</h3>
              </div>
              <div>
                {comments.map(comment => (
                  <div key={comment.id} style={{ 
                    padding: '15px', 
                    borderBottom: '1px solid #ddd',
                    backgroundColor: comment.userIsBlocked ? '#fff8e1' : 'white' 
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                      <div>
                        <strong>{comment.userName || 'User'}</strong>
                        {comment.userIsBlocked && (
                          <span style={{ 
                            marginLeft: '10px', 
                            color: '#ff6f00', 
                            fontWeight: 'bold',
                            fontSize: '12px',
                            padding: '2px 5px',
                            backgroundColor: '#fff8e1',
                            borderRadius: '3px'
                          }}>
                            BLOCKED USER
                          </span>
                        )}
                        <span style={{ marginLeft: '10px', color: '#666', fontSize: '12px' }}>
                          {formatDate(comment.createdAt)}
                        </span>
                      </div>
                      <div>
                        <button
                          onClick={() => handleDeleteComment(comment.id)}
                          disabled={actionInProgress}
                          style={{
                            padding: '3px 8px',
                            backgroundColor: '#f44336',
                            color: 'white',
                            border: 'none',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    <div style={{ marginBottom: '10px' }}>{comment.text}</div>
                    
                    {/* Replies section */}
                    {comment.replies && comment.replies.length > 0 && (
                      <div style={{ marginTop: '15px', marginLeft: '20px', borderLeft: '2px solid #ddd', paddingLeft: '15px' }}>
                        <h4 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>Replies:</h4>
                        {comment.replies.map(reply => (
                          <div key={reply.id} style={{ 
                            padding: '10px', 
                            marginBottom: '10px', 
                            backgroundColor: '#f9f9f9', 
                            borderRadius: '4px' 
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                              <div>
                                <strong>{reply.userName || 'User'}</strong>
                                <span style={{ marginLeft: '10px', color: '#666', fontSize: '12px' }}>
                                  {formatDate(reply.createdAt)}
                                </span>
                              </div>
                              <div>
                                <button
                                  onClick={() => handleDeleteReply(reply.id, comment.id)}
                                  disabled={actionInProgress}
                                  style={{
                                    padding: '2px 6px',
                                    backgroundColor: '#f44336',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '3px',
                                    cursor: 'pointer',
                                    fontSize: '11px'
                                  }}
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                            <div>{reply.text}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div style={{ padding: '20px', textAlign: 'center', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
          Please select a question to view its comments.
        </div>
      )}
    </div>
  );
}

export default CommentManagement;